import LoggingBase from '../../../../base/loggingbase';
import DomEventHelper from '../../../../utils/DomEventHelper';
import EventListenerManager from '../../../../utils/EventListenerManager';
import HtmHelper from '../../../../utils/HtmHelper';
import Validator from '../../../../utils/Validator';

/**
 * class SelectionCellRowHeightExtension - provides the interactive row-height adjustment
 * handle for a selection cell ({@link XCellItem}). It is a plain delegate: the host cell is
 * passed in and accessed via {@link #host}. The height-adjustment div itself is stored on
 * the host (it is also referenced by the host), the extension only manages it.
 */
export default class SelectionCellRowHeightExtension extends LoggingBase {

	/**
	 * constructs a new instance
	 * @param {*} host the host selection cell (XCellItem)
	 */
	constructor( host ) {
		super( 'widgets.xtw.rowheight.SelectionCellRowHeightExtension' );
		this._host = host;
		this.addHeightAdjustmentDiv();
	}

	/**
	 * @returns {*} the host selection cell
	 */
	get host() {
		return this._host;
	}

	/**
	 * @override
	 */
	doDestroy() {
		this.destroyHeightAdjustmentDiv();
		this._host = void 0;
		super.doDestroy();
	}

	reappendHeightAdjustmentDiv() {
		const host = this.host;
		if ( ![ host.element, host.heightAdjustmentDiv ]
			.every( element => element instanceof HTMLElement ) ) {
			return false;
		}
		host.element.appendChild( host.heightAdjustmentDiv );
		return true;
	}

	addHeightAdjustmentDiv() {
		const host = this.host;
		if ( !( host.element instanceof HTMLElement ) ) {
			return false;
		}
		host.heightAdjustmentDiv = this.newHeightAdjustmentDiv;
		host.element.appendChild( host.heightAdjustmentDiv );
		return true;
	}

	get newHeightAdjustmentDiv() {
		const heightAdjustmentDiv = window.document.createElement( "div" );
		heightAdjustmentDiv.classList.add( "row-height-adjustment" );
		this.addHeightAdjustmentDivBasicListeners( heightAdjustmentDiv );
		return heightAdjustmentDiv;
	}

	onHeightAdjustmentDivMouseDown( domEvent ) {
		DomEventHelper.stopIf( domEvent );
		const xRowItem = this.host.row;
		if ( !Validator.isObject( xRowItem ) ) {
			return false;
		}
		const xtwBody = this.host.xtwBody;
		if ( !Validator.isObjectPath( xtwBody, "xtwBody.xtdTbl" ) ||
			!Validator.isFunction( xtwBody.xtdTbl.onHeightAdjustmentDivMouseDown ) ) {
			return false;
		}
		return xtwBody.xtdTbl.onHeightAdjustmentDivMouseDown( domEvent, xRowItem );
	}

	onHeightAdjustmentDivMouseUp( domEvent ) {
		// DomEventHelper.stopIf( domEvent );
	}

	onHeightAdjustmentDivClick( domEvent ) {
		// DomEventHelper.stopIf( domEvent );
	}

	addHeightAdjustmentDivBasicListeners( heightAdjustmentDiv ) {
		const mousedownListenerSuccessfullyAdded = EventListenerManager.addListener( {
			instance: this,
			eventName: "mousedown",
			functionName: "onHeightAdjustmentDivMouseDown",
			callBackPrefix: "HeightAdjustmentDiv",
			element: heightAdjustmentDiv,
			useCapture: false
		} );
		const mouseupListenerSuccessfullyAdded = EventListenerManager.addListener( {
			instance: this,
			eventName: "mouseup",
			functionName: "onHeightAdjustmentDivMouseUp",
			callBackPrefix: "HeightAdjustmentDiv",
			element: heightAdjustmentDiv,
			useCapture: false
		} );
		const clickListenerSuccessfullyAdded = EventListenerManager.addListener( {
			instance: this,
			eventName: "click",
			functionName: "onHeightAdjustmentDivClick",
			callBackPrefix: "HeightAdjustmentDiv",
			element: heightAdjustmentDiv,
			useCapture: false
		} );
		return mousedownListenerSuccessfullyAdded &&
			mouseupListenerSuccessfullyAdded && clickListenerSuccessfullyAdded;
	}

	destroyHeightAdjustmentDiv() {
		const host = this.host;
		[ "mousedown", "mouseup", "click" ].forEach( eventName => {
			EventListenerManager.removeListener( this, eventName,
				host.heightAdjustmentDiv, "HeightAdjustmentDiv" );
		} );
		return HtmHelper.discardElementProperty( host, "heightAdjustmentDiv" );
	}

}
